import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Chip,
  Stack,
  Tooltip,
  Fade,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  AutoAwesome as AIIcon,
  Send as SendIcon,
  Gavel as GavelIcon,
  MenuBook as LawIcon,
  ShortText as ShorterIcon,
  Subject as LongerIcon,
  Edit as ImproveIcon,
  FormatQuote as FormalIcon,
  Lightbulb as SuggestIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface AIRewritePopoverProps {
  selectedText: string;
  context: string;
  onRewrite: (newText: string) => void;
  onClose: () => void;
  anchorPosition?: { top: number; left: number };
}

// Sugestões rápidas de ação
const quickActions = [
  { id: 'improve', label: 'Melhorar', icon: <ImproveIcon fontSize="small" />, prompt: 'Melhore a redação deste texto jurídico, tornando-o mais claro e profissional' },
  { id: 'formal', label: 'Mais Formal', icon: <FormalIcon fontSize="small" />, prompt: 'Reescreva este texto em um tom mais formal e técnico, adequado para documentos jurídicos' },
  { id: 'shorter', label: 'Resumir', icon: <ShorterIcon fontSize="small" />, prompt: 'Resuma este texto mantendo as informações essenciais, de forma mais concisa' },
  { id: 'longer', label: 'Expandir', icon: <LongerIcon fontSize="small" />, prompt: 'Expanda este texto com mais detalhes e argumentação jurídica' },
  { id: 'law', label: 'Adicionar Lei', icon: <LawIcon fontSize="small" />, prompt: 'Adicione referências a leis, artigos e dispositivos legais relevantes a este texto' },
  { id: 'jurisprudence', label: 'Jurisprudência', icon: <GavelIcon fontSize="small" />, prompt: 'Adicione referências a jurisprudências relevantes dos tribunais superiores' },
];

const AIRewritePopover: React.FC<AIRewritePopoverProps> = ({
  selectedText,
  context,
  onRewrite,
  onClose,
}) => {
  const [customPrompt, setCustomPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleRewrite = async (prompt: string) => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await api.post('/ai/rewrite-text', {
        selected_text: selectedText,
        context: context,
        instruction: prompt,
      });

      if (response.data.rewritten_text) {
        setResult(response.data.rewritten_text);
      } else {
        setError('Não foi possível reescrever o texto. Tente novamente.');
      }
    } catch (err: any) {
      console.error('Erro ao reescrever texto:', err);
      setError(err.response?.data?.detail || 'Erro ao processar a solicitação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action: typeof quickActions[0]) => {
    handleRewrite(action.prompt);
  };

  const handleCustomRewrite = () => {
    if (customPrompt.trim()) {
      handleRewrite(customPrompt);
    }
  };

  const handleApply = () => {
    if (result) {
      onRewrite(result);
      onClose();
    }
  };

  return (
    <Fade in={true}>
      <Paper
        elevation={8}
        sx={{
          width: '100%',
          maxWidth: 480,
          maxHeight: '80vh',
          overflow: 'hidden',
          borderRadius: 3,
          border: '2px solid #1a365d',
          bgcolor: 'white',
          boxSizing: 'border-box',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #1a365d 0%, #2d5a87 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
            <AIIcon sx={{ color: 'white' }} />
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'white' }}>
              Reescrever com IA
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2.5, bgcolor: 'white', maxHeight: 'calc(80vh - 60px)', overflowY: 'auto' }}>
          {/* Texto selecionado */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="caption" sx={{ color: '#1a365d', fontWeight: 600, fontSize: '0.8rem' }} gutterBottom display="block">
              Texto selecionado:
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: '#f0f4f8',
                maxHeight: 120,
                overflow: 'auto',
                borderRadius: 2,
                borderColor: '#cbd5e1',
                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
              }}
            >
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#334155', lineHeight: 1.6 }}>
                "{selectedText.length > 250 ? selectedText.substring(0, 250) + '...' : selectedText}"
              </Typography>
            </Paper>
          </Box>

          {/* Ações rápidas */}
          {!result && !loading && (
            <>
              <Typography variant="caption" sx={{ color: '#1a365d', fontWeight: 600, fontSize: '0.8rem', mb: 1.5 }} display="block">
                Ações rápidas:
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2.5 }}>
                {quickActions.map((action) => (
                  <Chip
                    key={action.id}
                    icon={action.icon}
                    label={action.label}
                    onClick={() => handleQuickAction(action)}
                    clickable
                    variant="outlined"
                    size="medium"
                    sx={{
                      color: '#1a365d',
                      borderColor: '#1a365d',
                      borderWidth: '1.5px',
                      fontWeight: 500,
                      py: 0.5,
                      '& .MuiChip-icon': {
                        color: '#1a365d',
                      },
                      '&:hover': {
                        bgcolor: '#1a365d',
                        color: 'white',
                        borderColor: '#1a365d',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 4px rgba(26,54,93,0.3)',
                        '& .MuiChip-icon': {
                          color: 'white',
                        },
                      },
                      transition: 'all 0.2s ease',
                    }}
                  />
                ))}
              </Stack>

              <Divider sx={{ my: 2.5 }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                  ou escreva sua instrução
                </Typography>
              </Divider>

              {/* Campo de instrução personalizada */}
              <TextField
                fullWidth
                multiline
                rows={2}
                placeholder="Ex: Adicione argumentação sobre prescrição, cite o art. 206 do CC..."
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                size="small"
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#f8fafc',
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1a365d',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1a365d',
                      borderWidth: 2,
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#1e293b',
                    fontSize: '0.9rem',
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#94a3b8',
                    opacity: 1,
                  },
                }}
              />

              <Button
                fullWidth
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleCustomRewrite}
                disabled={!customPrompt.trim()}
                sx={{
                  py: 1.2,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  color: 'white',
                  background: 'linear-gradient(135deg, #1a365d 0%, #2d5a87 100%)',
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 2px 8px rgba(26,54,93,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2d5a87 0%, #3d6a97 100%)',
                    boxShadow: '0 4px 12px rgba(26,54,93,0.4)',
                  },
                  '&.Mui-disabled': {
                    background: '#e2e8f0',
                    color: '#94a3b8',
                    boxShadow: 'none',
                  },
                }}
              >
                Reescrever com IA
              </Button>
            </>
          )}

          {/* Loading */}
          {loading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
              <CircularProgress size={48} sx={{ color: '#1a365d' }} />
              <Typography variant="body1" sx={{ mt: 2.5, color: '#1a365d', fontWeight: 500 }}>
                Processando com IA...
              </Typography>
              <Typography variant="caption" sx={{ mt: 0.5, color: '#64748b' }}>
                Isso pode levar alguns segundos
              </Typography>
            </Box>
          )}

          {/* Erro */}
          {error && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#fef2f2', borderRadius: 2, border: '1px solid #fecaca' }}>
              <Typography color="error" variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
                {error}
              </Typography>
              <Button 
                size="small" 
                variant="outlined"
                onClick={() => setError(null)} 
                sx={{ color: '#dc2626', borderColor: '#dc2626' }}
              >
                Tentar novamente
              </Button>
            </Box>
          )}

          {/* Resultado */}
          {result && (
            <>
              <Typography variant="caption" sx={{ color: '#166534', fontWeight: 600, fontSize: '0.8rem' }} gutterBottom display="block">
                ✓ Texto reescrito:
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: '#f0fdf4',
                  borderColor: '#22c55e',
                  borderWidth: '1.5px',
                  maxHeight: 200,
                  overflow: 'auto',
                  mb: 2.5,
                  borderRadius: 2,
                  boxShadow: 'inset 0 1px 3px rgba(34,197,94,0.1)',
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#14532d', lineHeight: 1.7 }}>
                  {result}
                </Typography>
              </Paper>

              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="contained"
                  onClick={handleApply}
                  fullWidth
                  startIcon={<AIIcon />}
                  sx={{
                    py: 1.2,
                    bgcolor: '#16a34a',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    textTransform: 'none',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(22,163,74,0.3)',
                    '&:hover': {
                      bgcolor: '#15803d',
                      boxShadow: '0 4px 12px rgba(22,163,74,0.4)',
                    },
                  }}
                >
                  Aplicar no documento
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setResult(null);
                    setCustomPrompt('');
                  }}
                  sx={{
                    color: '#1a365d',
                    borderColor: '#1a365d',
                    fontWeight: 500,
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 3,
                    '&:hover': {
                      bgcolor: '#f1f5f9',
                      borderColor: '#1a365d',
                    },
                  }}
                >
                  Nova
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </Paper>
    </Fade>
  );
};

export default AIRewritePopover;

